import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownRight, ArrowUpRight, Minus, Sparkles } from 'lucide-react';
import type { ASOTermRow } from '../api/client';

/* ASO ekranlarının paylaşılan sunum parçaları.
 *
 * Grafikler düz SVG ile çizilir — projede grafik kütüphanesi yok ve bu ekranlar için
 * (sparkline + tek çizgi) yeni bir bağımlılık eklemeye değmez. */

/** Popülerlik değerine göre renk: yüksek = yeşil, orta = mavi, düşük = kehribar/kırmızı. */
function popularityColor(value: number): string {
  if (value >= 70) return 'bg-emerald-500';
  if (value >= 40) return 'bg-blue-500';
  if (value >= 20) return 'bg-amber-500';
  return 'bg-red-400';
}

function trendStroke(delta: number): string {
  if (delta > 0) return 'stroke-emerald-500';
  if (delta < 0) return 'stroke-red-400';
  return 'stroke-gray-400';
}

/** Popülerlik barı — 1–100 ölçeği tek başına anlamsız olduğu için bar + sayı birlikte gösterilir. */
export const PopularityBar: React.FC<{ value: number; className?: string }> = ({ value, className }) => (
  <div className={`flex items-center gap-2 ${className ?? ''}`}>
    <div className="flex-1 min-w-[40px] h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
      <div
        className={`h-full rounded-full ${popularityColor(value)}`}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }}
      />
    </div>
    <span className="text-xs tabular-nums text-gray-600 dark:text-gray-300 w-6 text-right">{value}</span>
  </div>
);

/** Mini trend grafiği. Nokta yoksa çizim yapılmaz (tek dönemlik veri düz çizgi olarak yanıltıcı olur). */
export const Sparkline: React.FC<{ points: number[]; delta: number; width?: number; height?: number }> = ({
  points,
  delta,
  width = 70,
  height = 20,
}) => {
  if (!points || points.length < 2) {
    return <span className="text-xs text-gray-300 dark:text-gray-600">—</span>;
  }
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const stepX = width / (points.length - 1);
  const coords = points
    .map((p, i) => {
      const x = i * stepX;
      const y = height - ((p - min) / span) * (height - 3) - 1.5;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" className="inline-block">
      <polyline points={coords} fill="none" strokeWidth="1.5" className={trendStroke(delta)} />
    </svg>
  );
};

/** Haftalık değişim rozeti — trend bu üründeki asıl sinyal, bu yüzden her satırda görünür. */
export const DeltaBadge: React.FC<{ delta: number; isNew?: boolean }> = ({ delta, isNew }) => {
  const { t } = useTranslation();
  if (isNew) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400">
        <Sparkles className="w-3 h-3" />
        {t('aso.new')}
      </span>
    );
  }
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500">
        <Minus className="w-3 h-3" />
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium tabular-nums ${
        up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'
      }`}
    >
      {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {up ? '+' : ''}
      {delta}
    </span>
  );
};

/** Apple'ın kendi panelinde gösterdiği 1–5 ölçeği — kullanıcı bunu tanıdığı için korunur. */
export const Popularity5Badge: React.FC<{ value: number }> = ({ value }) => {
  if (!value) return null;
  return (
    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded text-[11px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 tabular-nums">
      {value}
    </span>
  );
};

/** Terimin uygulamaya göre durumu — gap analizinde satırın yanında görünür. */
export const FitBadge: React.FC<{ fit?: ASOTermRow['fit'] }> = ({ fit }) => {
  const { t } = useTranslation();
  if (!fit) return null;
  const map: Record<string, { cls: string; label: string }> = {
    missed: {
      cls: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      label: t('aso.fitMissed'),
    },
    working: {
      cls: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
      label: t('aso.fitWorking'),
    },
    dead_weight: {
      cls: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
      label: t('aso.fitDeadWeight'),
    },
  };
  const conf = map[fit];
  if (!conf) return null;
  return <span className={`text-[11px] px-1.5 py-0.5 rounded ${conf.cls}`}>{conf.label}</span>;
};

/** Genre kodunu okunur etikete çevirir: "PRODUCTIVITY_UTILITIES" -> "Productivity utilities".
 * Apple sabit bir genre enum'u yayınlamadığı için çeviri tablosu yerine biçimlendirme kullanılır. */
export function formatGenreLabel(genre: string): string {
  if (!genre) return '';
  const words = genre.replace(/_/g, ' ').toLowerCase().trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Ülke kodundan bayrak emoji (alpha-2). */
export function countryFlag(alpha2: string): string {
  if (!alpha2 || alpha2.length !== 2) return '';
  const a = alpha2.toUpperCase().charCodeAt(0);
  const b = alpha2.toUpperCase().charCodeAt(1);
  if (a < 65 || a > 90 || b < 65 || b > 90) return '';
  return String.fromCodePoint(0x1f1e6 + a - 65, 0x1f1e6 + b - 65);
}
